import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Clock, Crown, Users } from 'lucide-react'
import useCountdown from '../hooks/useCountdown'
import { formatDrawLeft } from '../utils/rushdropFormat'
import { hapticImpact } from '../../../utils/haptic'
import useAppStore from '../../../store/useAppStore'

/**
 * Карточка недельного розыгрыша ножа (блок weekly из GET /api/fast-event/).
 * Нет блока — нет карточки вовсе (розыгрыш выключен / приз не подобран / уже разыгран).
 * title показывается всегда целиком, description — под раскрытием, обычный текст
 * с переносами (white-space: pre-line). Таймер тикает локально по draw_in_seconds
 * с ресинхронизацией от поллинга; на нуле — «идёт розыгрыш», блок сам исчезнет
 * из следующего ответа API. Баллы/серия/участники приходят готовыми — ничего не считаем.
 */
const RushDropWeekly = ({ weekly }) => {
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const [isOpen, setIsOpen] = useState(false)
  const remaining = useCountdown(weekly?.draw_in_seconds)

  if (!weekly) return null

  const {
    skin,
    title,
    description,
    points,
    streak,
    next_points: nextPoints,
    participants
  } = weekly

  const isDrawing = remaining <= 0

  return (
    <section className="rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-yellow-400/10 to-orange-500/5 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]">
      <div className="flex items-center gap-2">
        <Crown size={14} className="text-yellow-400" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-yellow-400">
          {t('rushdrop.weekly.badge', 'Розыгрыш недели')}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-3">
        {skin?.image ? (
          <img
            src={skin.image}
            alt={skin.name || ''}
            className="h-20 w-20 shrink-0 object-contain"
            loading="lazy"
          />
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-white/5">
            <Crown size={28} className="text-yellow-400/60" />
          </div>
        )}
        <div className="min-w-0">
          {/* Заголовок — всегда целиком, без обрезки многоточием */}
          <p className="text-sm font-semibold leading-snug text-white">{title}</p>
          {skin?.type && (
            <p className="mt-1 text-[11px] text-text-secondary">{skin.type}</p>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-text-secondary">
          <Clock size={13} className="shrink-0 text-yellow-400" />
          {t('rushdrop.weekly.drawIn', 'До розыгрыша')}:
          <b className="tabular-nums text-yellow-300">
            {isDrawing
              ? t('rushdrop.weekly.drawing', 'Идёт розыгрыш…')
              : formatDrawLeft(remaining, t)}
          </b>
        </span>
        <span className="flex items-center gap-1 text-text-secondary">
          <Users size={13} className="shrink-0" />
          {t('rushdrop.weekly.participants', { count: Number(participants) || 0 })}
        </span>
      </div>

      {isGuest ? (
        <button type="button" onClick={openLogin} className="mt-3 rounded-lg bg-accent-primary px-4 py-2 text-xs font-bold text-dark-primary">{t('guest.login')}</button>
      ) : (
        <p className="mt-2 text-xs text-text-secondary">
          {t('rushdrop.weekly.yourPoints', 'Твои баллы')}:{' '}
          <b className="tabular-nums text-white">{Number(points) || 0}</b>
          {' · '}
          {Number(streak) > 0
            ? `${t('rushdrop.weekly.streak', { count: Number(streak) || 0 })} → ${t('rushdrop.weekly.nextPoints', { count: Number(nextPoints) || 0 })}`
            : t('rushdrop.weekly.startStreak', { count: Number(nextPoints) || 0 })}
        </p>
      )}

      {description && (
        <div className="mt-3 border-t border-white/5 pt-3">
          <button
            type="button"
            onClick={() => {
              hapticImpact('light')
              setIsOpen((prev) => !prev)
            }}
            className="flex items-center gap-1 text-xs font-semibold text-yellow-300 transition-all active:scale-[0.98]"
          >
            {isOpen
              ? t('rushdrop.weekly.less', 'Скрыть')
              : t('rushdrop.weekly.more', 'Подробнее')}
            <ChevronDown
              size={14}
              className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isOpen && (
            <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-text-secondary">
              {description}
            </p>
          )}
        </div>
      )}
    </section>
  )
}

export default RushDropWeekly
